import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useInternalTransfer } from "@/hooks/use-mutations";
import { getInitials } from "@/lib/utils";
import { useModalStore } from "@/zustand/modalStore";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useState } from "react";

const TransferOtp = ({
  payee,
  amount,
  asset,
  onDone,
}: {
  payee: Beneficiary;
  amount: string;
  asset: any;
  onDone: () => void;
}) => {
  const [otp, setOtp] = useState("");
  const { openModal } = useModalStore();
  const symbol = asset?.token.symbol ?? "";
  const payeeName = `${payee.firstName} ${payee.lastName}`.trim();

  const { mutate: transfer, isPending } = useInternalTransfer({
    onSuccess: () => {
      // Reset the sheet first, so dismissing the success modal lands on the
      // entry screen rather than the pre-filled amount form.
      onDone();
      openModal("success", { type: "internal-transfer" });
    },
  });

  return (
    <div className="relative z-[999]">
      <h3 className="mt-6 text-xl font-semibold">Enter OTP</h3>

      <div className="mt-6 flex items-center gap-4">
        <div className="bg-dark-primary-gradient text-dark-text-400 flex size-12 shrink-0 items-center justify-center rounded-full font-medium">
          {getInitials(payeeName)}
        </div>
        <div className="min-w-0">
          <p className="text-xl">{payeeName}</p>
          <p className="text-dark-text-300 truncate text-sm">{payee.email}</p>
        </div>
      </div>

      <p className="mt-6 text-sm">
        Enter the OTP sent to your email to complete the transfer of{" "}
        <span className="font-semibold">
          {amount} {symbol}
        </span>
        .
      </p>

      <div className="mt-6">
        <InputOTP
          autoFocus
          autoComplete="off"
          pattern={REGEXP_ONLY_DIGITS}
          maxLength={6}
          value={otp}
          onChange={setOtp}
        >
          <InputOTPGroup className="*:text-dark-text-50 *:bg-dark-card-5 *:border-dark-stroke-5 gap-3 *:!rounded *:border *:shadow-none">
            <InputOTPSlot index={0} />
            <InputOTPSlot index={1} />
            <InputOTPSlot index={2} />
            <InputOTPSlot index={3} />
            <InputOTPSlot index={4} />
            <InputOTPSlot index={5} />
          </InputOTPGroup>
        </InputOTP>
      </div>

      <Button
        isLoading={isPending}
        disabled={otp.length !== 6 || isPending}
        onClick={() =>
          transfer({
            amount: Number(amount),
            toUserId: payee.id,
            // tokenId, not asset.id — see InternalTransferPayload.
            assetId: asset.tokenId,
            otp,
          })
        }
        className="text-dark-text-400 bg-dark-primary-gradient mt-6 h-12 w-full rounded-lg disabled:opacity-50"
      >
        Complete Transfer
      </Button>
    </div>
  );
};

export default TransferOtp;
